import { DataExtractor } from './auditDataExtractor';
import { FormatDetector } from './auditFormatDetector';
import { TransformationValidator } from './auditTransformationValidator';
import { TransformationCache } from './auditTransformationCache';
import { TransformationAnalyzer } from './auditTransformationAnalyzer';

// AuditTransformer - Core transformation orchestrator
export class AuditTransformer {
  constructor() {
    // Initialize sub-modules
    this.dataExtractor = new DataExtractor();
    this.formatDetector = new FormatDetector();
    this.validator = new TransformationValidator();
    this.cache = new TransformationCache();
    this.analyzer = new TransformationAnalyzer();
    
    // Core statistics
    this.stats = {
      totalTransformations: 0,
      successfulTransformations: 0,
      failedTransformations: 0,
      lastTransformationTime: null
    };
  }

  // 🔥 Main transformation method for cumulative data
  transformCumulativeAuditDataToFieldResponses(cumulativeData, auditFormData) {
    console.log('🔧 ENHANCED TRANSFORMATION: Starting comprehensive data analysis...');
    
    const startTime = Date.now();
    this.stats.totalTransformations++;
    this.stats.lastTransformationTime = new Date().toISOString();
    
    try {
      // Check cache first
      const cacheKey = this.cache.generateCacheKey(cumulativeData, auditFormData);
      const cachedResult = this.cache.get(cacheKey);
      if (cachedResult) {
        console.log('✅ Using cached transformation result');
        return cachedResult;
      }
      
      // Validate inputs
      const inputValidation = this.validateInputs(cumulativeData, auditFormData);
      if (!inputValidation.isValid) {
        console.error('❌ Input validation failed:', inputValidation.errors);
        this.stats.failedTransformations++;
        return [];
      }
      
      console.log('📊 Input validation passed');
      console.log('  - Categories in cumulative data:', Object.keys(cumulativeData));
      console.log('  - Audit form data structure:', Object.keys(auditFormData));
      
      const fieldResponses = [];
      let totalChecksProcessed = 0;
      let failedChecksFound = 0;
      
      // Process each category
      Object.keys(cumulativeData).forEach(categoryId => {
        const categoryData = cumulativeData[categoryId];
        console.log(`\n📂 Processing category ${categoryId}`);
        
        if (!categoryData || !categoryData.controlItems) {
          console.log(`  ⚠️ No control items in category ${categoryId}`);
          return;
        }
        
        // Process control items in this category
        Object.keys(categoryData.controlItems).forEach(controlItemId => {
          const controlItemData = categoryData.controlItems[controlItemId];
          console.log(`\n  📋 Processing control item ${controlItemId}`);
          
          if (!controlItemData || !controlItemData.checks) {
            console.log(`    ⚠️ No checks in control item ${controlItemId}`);
            return;
          }
          
          // Process checks in this control item
          Object.keys(controlItemData.checks).forEach(checkId => {
            const checkData = controlItemData.checks[checkId];
            totalChecksProcessed++;
            
            console.log(`\n    🔍 Processing check ${checkId}`);
            console.log('      Check data:', checkData);
            console.log('      Is checked (failed):', checkData.isChecked);
            
            // Only process FAILED checks (isChecked = true)
            if (!checkData.isChecked) {
              console.log('      ✅ Check passed, skipping');
              return;
            }
            
            failedChecksFound++;
            console.log('      ❌ Check FAILED, creating field response...');
            
            // Extract details using data extractor
            const extractedData = this.dataExtractor.extractAllDetails(
              checkData, checkId, controlItemData, controlItemId, categoryData, categoryId, auditFormData
            );
            
            console.log('      📋 Extracted details:', extractedData);
            
            // Create field response
            const fieldResponse = this.createFieldResponse(checkData, extractedData);
            fieldResponses.push(fieldResponse);
            
            console.log('      ✅ Field response created successfully');
          });
        });
      });
      
      console.log(`\n🎯 TRANSFORMATION SUMMARY:`);
      console.log(`  Total checks processed: ${totalChecksProcessed}`);
      console.log(`  Failed checks found: ${failedChecksFound}`);
      console.log(`  Field responses generated: ${fieldResponses.length}`);
      
      if (fieldResponses.length === 0 && totalChecksProcessed > 0) {
        console.log('⚠️ WARNING: No field responses generated despite processing checks');
        console.log('This usually means all checks passed (isChecked = false)');
      }
      
      // Cache the result
      this.cache.set(cacheKey, fieldResponses);
      this.stats.successfulTransformations++;
      
      const duration = Date.now() - startTime;
      console.log(`✅ Transformation completed in ${duration}ms`);
      
      return fieldResponses;
      
    } catch (error) {
      console.error('❌ Error during transformation:', error);
      this.stats.failedTransformations++;
      return [];
    }
  }

  // Transform legacy checklist format
  transformChecklistToFieldResponses(checklistData, auditFormData) {
    console.log('🔧 Starting legacy checklist transformation...');
    
    if (!checklistData || !auditFormData) {
      console.log('❌ Missing required data for transformation');
      return [];
    }

    // Check if this is actually cumulative format
    if (this.formatDetector.isCumulativeDataFormat(checklistData)) {
      console.log('✅ Detected cumulative data format, redirecting to cumulative transformation');
      return this.transformCumulativeAuditDataToFieldResponses(checklistData, auditFormData);
    }
    
    console.log('📋 Using legacy transformation format');
    
    const fieldResponses = [];
    
    try {
      Object.keys(checklistData).forEach(categoryId => {
        const categoryData = checklistData[categoryId];
        
        Object.keys(categoryData.controlItems || {}).forEach(controlItemId => {
          const controlItemData = categoryData.controlItems[controlItemId];
          
          Object.keys(controlItemData.checks || {}).forEach(checkId => {
            const checkData = controlItemData.checks[checkId];
            
            if (checkData.isCompleted) {
              // Use hierarchy data to get details
              const check = auditFormData.hierarchy?.checks?.find(c => c.check_id.toString() === checkId);
              const controlItem = auditFormData.hierarchy?.control_items?.find(ci => ci.control_item_id.toString() === controlItemId);
              const category = auditFormData.hierarchy?.categories?.find(cat => cat.category_id.toString() === categoryId);
              
              const fieldResponse = {
                field_builder_id: checkData.fieldBuilderId || null,
                category_id: category?.category_id || null,
                control_item_id: controlItem?.control_item_id || null,
                check_id: check?.check_id || null,
                field_label: checkData.label || '',
                category_title: category?.category_name || '',
                selected_points: checkData.selectedPoints || 0,
                quantity: checkData.quantity || 0,
                remark: checkData.comment || '',
                production_phase: checkData.productionPhase || '',
                is_fixed_before_shipment: checkData.isFixedBeforeShipment || false,
                image_1_url: checkData.images?.[0]?.url || '',
                image_2_url: checkData.images?.[1]?.url || '',
                image_3_url: checkData.images?.[2]?.url || '',
                additional_data: {
                  isChecked: checkData.isChecked,
                  deductionPoints: checkData.deductionPoints,
                  measurementUnit: checkData.measurementUnit,
                  isIndependent: true,
                  transformationType: 'legacy'
                }
              };
              
              fieldResponses.push(fieldResponse);
            }
          });
        });
      });
      
      console.log(`🎯 Legacy transformation complete: ${fieldResponses.length} field responses`);
      return fieldResponses;
      
    } catch (error) {
      console.error('❌ Error in legacy transformation:', error);
      return [];
    }
  }

  // Create field response object
  createFieldResponse(checkData, extractedData) {
    return {
      field_builder_id: checkData.fieldBuilderId || null,
      category_id: extractedData.categoryDetails.category_id,
      control_item_id: extractedData.controlItemDetails.control_item_id,
      check_id: extractedData.checkDetails.check_id,
      field_label: extractedData.checkDetails.check_code || extractedData.controlItemDetails.control_item_code || `Check ${extractedData.checkId}`,
      category_title: extractedData.categoryDetails.category_name || `Category ${extractedData.categoryId}`,
      selected_points: extractedData.checkDetails.deduction_points || 0,
      quantity: checkData.quantity || 1,
      remark: checkData.comment || '',
      production_phase: checkData.productionPhase || '',
      is_fixed_before_shipment: checkData.isFixedBeforeShipment || false,
      image_1_url: checkData.images?.[0]?.url || '',
      image_2_url: checkData.images?.[1]?.url || '',
      image_3_url: checkData.images?.[2]?.url || '',
      additional_data: {
        isChecked: checkData.isChecked,
        deductionPoints: extractedData.checkDetails.deduction_points || 0,
        measurementUnit: extractedData.checkDetails.measurement_unit || '',
        lastUpdated: checkData.lastUpdated || new Date().toISOString(),
        transformedAt: new Date().toISOString(),
        originalCheck: checkData.originalCheck,
        isIndependent: true,
        transformationType: 'cumulative'
      }
    };
  }

  // Validate transformation inputs
  validateInputs(cumulativeData, auditFormData) {
    const errors = [];
    
    if (!cumulativeData || typeof cumulativeData !== 'object') {
      errors.push('Invalid cumulative data: must be an object');
    }
    
    if (!auditFormData) {
      errors.push('Missing audit form data');
    }
    
    if (Object.keys(cumulativeData || {}).length === 0) {
      errors.push('Cumulative data is empty');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Transform with advanced options
  transformWithOptions(data, auditFormData, options = {}) {
    const {
      useCache = true,
      validateOutput = true,
      includeDebugInfo = false,
      transformationType = 'auto' // 'auto', 'cumulative', 'legacy'
    } = options;
    
    const startTime = Date.now();
    
    // Disable cache if requested
    if (!useCache) {
      const originalCacheSize = this.cache.size();
      this.cache.clear();
      console.log(`🚫 Cache disabled for this transformation (cleared ${originalCacheSize} items)`);
    }
    
    let result;
    let transformationMethod;
    
    try {
      // Determine transformation method
      if (transformationType === 'auto') {
        if (this.formatDetector.isCumulativeDataFormat(data)) {
          result = this.transformCumulativeAuditDataToFieldResponses(data, auditFormData);
          transformationMethod = 'cumulative';
        } else {
          result = this.transformChecklistToFieldResponses(data, auditFormData);
          transformationMethod = 'legacy';
        }
      } else if (transformationType === 'cumulative') {
        result = this.transformCumulativeAuditDataToFieldResponses(data, auditFormData);
        transformationMethod = 'cumulative';
      } else if (transformationType === 'legacy') {
        result = this.transformChecklistToFieldResponses(data, auditFormData);
        transformationMethod = 'legacy';
      } else {
        throw new Error(`Unknown transformation type: ${transformationType}`);
      }
      
      const duration = Date.now() - startTime;
      
      // Validate output if requested
      let validation = null;
      if (validateOutput) {
        validation = this.validator.validateAllFieldResponses(result);
      }
      
      const response = {
        fieldResponses: result,
        transformationMethod,
        duration,
        success: true
      };
      
      if (validation) {
        response.validation = validation;
        response.success = validation.isValid;
      }
      
      if (includeDebugInfo) {
        response.debugInfo = {
          inputDataSize: JSON.stringify(data).length,
          outputCount: result.length,
          cacheStats: this.cache.getStats(),
          dataAnalysis: this.analyzer.analyzeDataStructure(data),
          options
        };
      }
      
      return response;
      
    } catch (error) {
      console.error('❌ Error in transformWithOptions:', error);
      return {
        fieldResponses: [],
        transformationMethod: 'error',
        duration: Date.now() - startTime,
        success: false,
        error: error.message
      };
    }
  }

  // Batch transformation for multiple data sets
  batchTransform(dataSets, auditFormData, options = {}) {
    console.log(`🔄 Starting batch transformation for ${dataSets.length} data sets...`);
    
    const results = [];
    const startTime = Date.now();
    
    dataSets.forEach((data, index) => {
      try {
        const result = this.transformWithOptions(data, auditFormData, {
          ...options,
          includeDebugInfo: false // Disable debug info for batch to save memory
        });
        
        results.push({
          index,
          success: result.success,
          fieldResponseCount: result.fieldResponses.length,
          transformationMethod: result.transformationMethod,
          duration: result.duration,
          validation: result.validation,
          error: result.error
        });
        
      } catch (error) {
        results.push({
          index,
          success: false,
          error: error.message,
          fieldResponseCount: 0
        });
      }
    });
    
    const totalDuration = Date.now() - startTime;
    const successCount = results.filter(r => r.success).length;
    
    console.log(`✅ Batch transformation completed: ${successCount}/${dataSets.length} successful in ${totalDuration}ms`);
    
    return {
      results,
      summary: {
        totalDataSets: dataSets.length,
        successfulTransformations: successCount,
        failedTransformations: dataSets.length - successCount,
        totalDuration,
        averageDuration: totalDuration / dataSets.length
      }
    };
  }

  // Delegate methods to sub-modules
  isCumulativeDataFormat(data) {
    return this.formatDetector.isCumulativeDataFormat(data);
  }

  validateFieldResponse(fieldResponse) {
    return this.validator.validateFieldResponse(fieldResponse);
  }

  validateAllFieldResponses(fieldResponses) {
    return this.validator.validateAllFieldResponses(fieldResponses);
  }

  analyzeDataStructure(data) {
    return this.analyzer.analyzeDataStructure(data);
  }

  getTransformationSummary(originalData, transformedData) {
    return this.analyzer.getTransformationSummary(originalData, transformedData, this.cache.getStats(), this.stats);
  }

  debugTransformation(data, auditFormData) {
    console.log('🔍 DEBUG INDEPENDENT TRANSFORMATION:');
    console.log('Input data:', data);
    console.log('AuditFormData:', auditFormData);
    
    const result = this.transformWithOptions(data, auditFormData, {
      includeDebugInfo: true,
      validateOutput: true
    });
    
    console.log('🔍 Debug transformation result:', result);
    return result;
  }

  // Cache management methods
  clearCache() {
    return this.cache.clear();
  }

  getCacheStats() {
    return this.cache.getStats();
  }

  // Statistics methods
  getStats() {
    return {
      ...this.stats,
      cacheStats: this.cache.getStats(),
      validatorStats: this.validator.getStats(),
      analyzerStats: this.analyzer.getStats()
    };
  }

  resetStats() {
    this.stats = {
      totalTransformations: 0,
      successfulTransformations: 0,
      failedTransformations: 0,
      lastTransformationTime: null
    };
    
    // Reset sub-module stats
    this.validator.resetStats();
    this.analyzer.resetStats();
    
    console.log('📊 Transformation statistics reset');
  }

  // Export transformer data
  exportTransformerData() {
    return {
      exportedAt: new Date().toISOString(),
      stats: this.getStats(),
      cacheData: this.cache.export(),
      validatorData: this.validator.export(),
      analyzerData: this.analyzer.export()
    };
  }

  // Reset entire transformer
  reset() {
    this.cache.clear();
    this.resetStats();
    this.validator.reset();
    this.analyzer.reset();
    console.log('🔄 Transformer reset complete');
  }
}